/**
 * ControlRoom — /admin · the Phase 1a dashboard.
 *
 * Renders the 8 health cards from /api/os-admin/dashboard/health and
 * the static Requests preview table. The live Requests queue lands
 * with Admin 1b reading lc_concierge_requests.
 */
import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";

export default function ControlRoom() {
  const [health, setHealth] = useState(null);
  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [h, p] = await Promise.all([
          api.get("/os-admin/dashboard/health"),
          api.get("/os-admin/dashboard/requests-preview"),
        ]);
        setHealth(h.data);
        setPreview(p.data);
      } catch (e) {
        setErr(formatApiError(e.response?.data?.detail) || e.message);
      }
    })();
  }, []);

  const stamp = health?.generated_at
    ? new Date(health.generated_at).toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

  return (
    <div data-testid="os-control-room">
      <div className="os-pagehead">
        <h1>Control Room</h1>
        <span className="count">{stamp ? `live as of ${stamp} IST` : "loading…"}</span>
      </div>
      <div className="os-pagesub">
        What needs you, what&apos;s broken, and what the member can see right now.
      </div>

      {err && (
        <div
          style={{
            background: "rgba(199,106,91,0.08)",
            border: "1px solid rgba(199,106,91,0.4)",
            color: "#E5B6AE",
            padding: "10px 14px",
            borderRadius: 6,
            marginBottom: 16,
            fontSize: 12.5,
          }}
          data-testid="control-room-error"
        >
          {err}
        </div>
      )}

      {/* HEALTH CARDS */}
      <div className="os-hcards" data-testid="os-health-cards">
        {!health &&
          Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="os-hcard" data-testid={`os-health-card-skeleton-${i}`}>
              <div className="lab"><span className="dot os-d-muted" />…</div>
              <div className="big os-t-muted">·</div>
            </div>
          ))}
        {health?.cards.map((card) => {
          const isNumeric = typeof card.value === "number" || /^\d/.test(String(card.value ?? ""));
          return (
            <div
              key={card.key}
              className="os-hcard"
              data-testid={`os-health-card-${card.key}`}
            >
              {card.placeholder && <span className="ph-tag">pending</span>}
              <div className="lab">
                <span className={`dot os-d-${card.tone || "muted"}`} />
                {card.label}
              </div>
              <div className={`big ${isNumeric ? "" : "is-text"} os-t-${card.tone || "muted"}`}>
                {card.value ?? "—"}
              </div>
              {card.meta && <div className="meta">{card.meta}</div>}
            </div>
          );
        })}
      </div>

      {/* REQUESTS PREVIEW */}
      <div className="os-section-h">Requests needing action</div>
      <div
        style={{
          background: "rgba(194, 160, 99, 0.06)",
          border: "1px solid var(--os-hair-soft)",
          borderRadius: 6,
          padding: "8px 14px",
          marginBottom: 14,
          fontSize: 11.5,
          letterSpacing: "0.06em",
          color: "var(--os-brass-bright)",
          fontFamily: "var(--os-font-mono)",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
        data-testid="os-preview-banner"
      >
        <span style={{ fontWeight: 700, letterSpacing: "0.18em", textTransform: "uppercase" }}>Static preview</span>
        <span>·</span>
        <span style={{ color: "var(--os-muted)" }}>live queue lands with Admin 1b</span>
      </div>
      <div className="os-toolbar">
        <button className="os-fchip on" disabled>All</button>
        <button className="os-fchip" disabled>Urgent</button>
        <button className="os-fchip" disabled>High-risk</button>
        <button className="os-fchip" disabled>Unassigned</button>
        <button className="os-fchip" disabled>Table</button>
        <button className="os-fchip" disabled>Enjoy</button>
        <button className="os-fchip" disabled>Escape</button>
        <div className="os-spacer" />
      </div>

      <table className="os-tbl" data-testid="os-requests-preview-table">
        <thead>
          <tr>
            <th>Ref</th>
            <th>Surface</th>
            <th>Request</th>
            <th>Member</th>
            <th>Risk</th>
            <th>Owner</th>
            <th>Status</th>
            <th>Zoho</th>
          </tr>
        </thead>
        <tbody>
          {!preview &&
            Array.from({ length: 4 }).map((_, i) => (
              <tr key={i}>
                <td colSpan={8} style={{ color: "var(--os-faint)" }}>…</td>
              </tr>
            ))}
          {preview?.rows.map((r) => (
            <tr key={r.ref} data-testid={`os-preview-row-${r.ref}`}>
              <td className="os-ref">{r.ref}</td>
              <td><span className="os-surf">{r.surface}</span></td>
              <td>{r.request}</td>
              <td>{r.member}</td>
              <td>
                {r.risk === "HIGH"
                  ? <span className="os-risk-high">HIGH</span>
                  : <span className="os-risk-low">low</span>}
              </td>
              <td>
                <span className="os-owner">
                  <span className="av">{r.owner === "Unassigned" ? "—" : r.owner[0]}</span>
                  {r.owner}
                </span>
              </td>
              <td>
                <span className={`os-pill ${statusClass(r.status)}`}>{r.status}</span>
              </td>
              <td className="os-ref">{r.zoho || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <p
        style={{
          fontSize: 11.5,
          color: "var(--os-faint)",
          marginTop: 12,
          fontStyle: "italic",
        }}
        data-testid="os-preview-note"
      >
        {preview?.note ||
          "Static preview · the live queue lands with Admin 1b reading lc_concierge_requests."}
      </p>
    </div>
  );
}

function statusClass(status) {
  if (!status) return "";
  const s = status.toLowerCase();
  if (s.includes("urgent")) return "os-p-urgent";
  if (s.includes("progress")) return "os-p-prog";
  if (s.includes("new")) return "os-p-new";
  if (s.includes("confirm") || s.includes("done")) return "os-p-done";
  return "";
}
